import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  InputAdornment,
  inputBaseClasses,
  TextField,
} from "@mui/material";
import { Clear, DeleteForeverRounded } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { Transition } from "../../pages/app/utils/animation";
import { useCase } from "../../store/case/hooks";
import { useModals } from "../../store/dialogs/hooks";
import z from "zod";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

const DeleteCaseSchema = (name) =>
  z.object({
    caseName: z
      .string()
      .trim()
      .min(1, { message: `The name of the case (${name}) is required.` })
      .refine((val) => val === name, {
        message: `Name must match the case name: ${name}.`,
      }),
  });

export const ConfirmDeleteCaseDialog = ({ af_case }) => {
  const { deleteCase, reset: resetCase } = useCase();
  const { closeDeleteCaseDialog, caseDetailsCaseId, showDeleteCaseDialog } =
    useModals();
  const navigate = useNavigate();

  const {
    control,
    handleSubmit,
    reset: resetForm,
    trigger,
    watch,
    formState: { errors, isValid },
  } = useForm({
    resolver: zodResolver(DeleteCaseSchema(af_case?.caseName)),
    defaultValues: {
      caseName: "",
    },
    mode: "all",
  });

  const watchCaseName = watch("caseName");

  const handleClose = () => {
    resetForm();
    closeDeleteCaseDialog();
  };

  const handleClear = () => {
    resetForm();
    trigger("caseName");
  };

  const onSubmit = async () => {
    await deleteCase(af_case);
    await resetCase();
    resetForm();
    closeDeleteCaseDialog();
    navigate("/app/cases"); // don't like that this navigates instead of just refreshing the page
  };

  return (
    <Dialog
      aria-hidden={!showDeleteCaseDialog}
      open={showDeleteCaseDialog && caseDetailsCaseId === af_case.id}
      onClose={handleClose}
      closeAfterTransition={false}
      slots={{
        transition: Transition,
      }}
    >
      <Box component="form" onSubmit={handleSubmit(onSubmit)}>
        <DialogTitle>DELETE CASE</DialogTitle>
        <DialogContent dividers>
          <Alert
            severity="warning"
            variant="filled"
            sx={{ boxShadow: 1, mb: 3 }}
          >
            This action deletes all clips in this case. This action cannot be
            undone.
          </Alert>
          <DialogContentText>
            To continue please type the name of this Case in the field below.
          </DialogContentText>
          <Controller
            name="caseName"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                error={!!errors.caseName}
                fullWidth
                helperText={
                  errors.caseName
                    ? `${errors.caseName?.message}`
                    : `${af_case?.caseName}`
                }
                label="Case Name"
                required
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment
                        position="end"
                        sx={{
                          [`[data-shrink=false] ~ .${inputBaseClasses.root} > &`]:
                            {
                              opacity: 0,
                              pointerEvents: "none",
                            },
                        }}
                      >
                        <IconButton
                          aria-label="clear input"
                          onClick={handleClear}
                          edge="end"
                          size="small"
                        >
                          <Clear />
                        </IconButton>
                      </InputAdornment>
                    ),
                  },
                }}
                sx={{ mt: 3 }}
              />
            )}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} variant="outlined">
            Cancel
          </Button>
          <Button
            color="error"
            disabled={!isValid || af_case?.caseName !== watchCaseName}
            startIcon={<DeleteForeverRounded />}
            type="submit"
            variant="contained"
          >
            DELETE CASE
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
};
